/**
 * A drag of pills from one box to another in the same scope (the To, CC and
 * BCC of one email step, say). The browser carries only text between pages,
 * so what's dragged stays here while the drag lasts: the box it's dropped on
 * adds the items, and the box it came from takes them out when the drag ends.
 */

/** What a drag carries: typed addresses, and anything else its host moves. */
export interface PillDragItems {
  emails?: string[]
  [kind: string]: unknown
}

interface PillDrag {
  scope: string
  /** The box the pills came from, which can't take them. */
  source: string
  items: PillDragItems
  dropped: boolean
}

let current: PillDrag | null = null

export function beginPillDrag(scope: string, source: string, items: PillDragItems) {
  current = { scope, source, items, dropped: false }
}

/** The drag over a box in `scope`, unless it came from that box. */
export function pillDragOver(scope: string, target: string): PillDragItems | null {
  return current && current.scope === scope && current.source !== target ? current.items : null
}

/** The drag over the box it came from (`source`), which can reorder its pills. */
export function pillDragFromHere(scope: string, source: string): PillDragItems | null {
  return current && current.scope === scope && current.source === source ? current.items : null
}

/** The box it was dropped on has taken the items. */
export function markPillDragDropped() {
  if (current) current.dropped = true
}

/** Ends the drag: whether another box took the pills, so the source lets them go. */
export function endPillDrag(source: string): boolean {
  const moved = current?.source === source && current.dropped
  if (current?.source === source) current = null
  return moved
}

/** Addresses as they paste anywhere: `a@x.com; b@y.com`. */
export function joinEmails(emails: readonly string[]) {
  return emails.join('; ')
}
